import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, X, ExternalLink, AlertTriangle, ArrowRight, FileText, Building2, Calendar, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TINTS = {
    received: "bg-blue-50 text-blue-700",
    in_design: "bg-cyan-50 text-cyan-700",
    in_production: "bg-amber-50 text-amber-700",
    quality_check: "bg-purple-50 text-purple-700",
    ready: "bg-emerald-50 text-emerald-700",
    delivered: "bg-slate-100 text-slate-600",
    cancelled: "bg-rose-50 text-rose-600",
};

const INVOICE_STATUS_TINTS = {
    paid: "bg-emerald-50 text-emerald-700",
    unpaid: "bg-amber-50 text-amber-700",
    overdue: "bg-rose-50 text-rose-700",
    cancelled: "bg-slate-100 text-slate-500",
};

/**
 * Side-sheet that drills into a metric on the Admin Dashboard.
 *
 * Two new pieces vs. the previous version:
 *   1. Left side (visible on lg+ screens only) now renders a `<DrillSummary>`
 *      panel with mini KPIs computed from the visible rows — total count,
 *      total value when applicable, status histogram, oldest record date.
 *   2. Invoice rows are now clickable: PDF opens in a new tab, "Open" emits
 *      `admin-switch-tab` to the invoices tab AND fires `admin-open-invoice`
 *      with the invoice id so Admin.jsx can pop the existing detail modal.
 */
export default function DrillDrawer({ open, title, fetchUrl, kind: explicitKind, onClose, onPick }) {
    const [rows, setRows] = useState([]);
    const [kind, setKind] = useState(explicitKind);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !fetchUrl) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setKind(explicitKind);
        axios.get(`${API}${fetchUrl}`)
            .then((r) => {
                if (cancelled) return;
                // Backend returns either a raw array OR { kind, metric, rows, count }.
                // Normalise both shapes so the table rendering stays simple.
                const payload = r.data;
                if (Array.isArray(payload)) {
                    setRows(payload);
                } else if (payload && Array.isArray(payload.rows)) {
                    setRows(payload.rows);
                    if (payload.kind) setKind(payload.kind);
                } else {
                    setRows([]);
                }
            })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, fetchUrl, explicitKind]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex" data-testid="drill-drawer">
            {/* Backdrop with a useful summary panel (lg+) */}
            <button
                type="button"
                onClick={onClose}
                aria-label="Close drill-down"
                className="flex-1 hidden lg:flex bg-slate-900/40 backdrop-blur-sm items-stretch justify-end"
            >
                {/* The summary panel itself — sits inside the backdrop button.
                    stopPropagation so clicks inside the panel don't close the drawer. */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-[min(420px,40vw)] mr-auto ml-12 my-12 self-start text-left cursor-default"
                >
                    <DrillSummary kind={kind} title={title} rows={rows} loading={loading} />
                </div>
            </button>
            {/* Mobile backdrop — plain dim, click closes. */}
            <button
                type="button"
                onClick={onClose}
                aria-label="Close drill-down"
                className="flex-1 lg:hidden bg-slate-900/40 backdrop-blur-sm"
            />

            {/* The drawer itself */}
            <div className="w-full max-w-[460px] bg-white shadow-2xl overflow-y-auto animate-slide-in">
                <div className="sticky top-0 bg-white z-10 px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-slate-500">· Drill-down</div>
                        <div className="font-display text-lg tracking-tight text-slate-900 truncate">{title}</div>
                    </div>
                    <button onClick={onClose} data-testid="drill-close" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><X size={16} /></button>
                </div>

                <div className="py-2">
                    {loading ? (
                        <div className="flex items-center gap-2 px-5 py-8 text-[13.5px] text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                    ) : error ? (
                        <div className="flex items-center gap-2 px-5 py-6 text-[13.5px] text-rose-600"><AlertTriangle size={14} /> {error}</div>
                    ) : rows.length === 0 ? (
                        <div className="px-5 py-10 text-center text-[13.5px] text-slate-500">No records.</div>
                    ) : kind === "invoices" ? (
                        <InvoiceList rows={rows} onClose={onClose} />
                    ) : kind === "clients" ? (
                        <ClientList rows={rows} onClose={onClose} />
                    ) : (
                        <CaseList rows={rows} onClose={onClose} onPick={onPick} />
                    )}
                </div>

                <style>{`@keyframes slideIn{from{transform:translateX(20px);opacity:0}to{transform:translateX(0);opacity:1}}.animate-slide-in{animation:slideIn .25s ease-out}`}</style>
            </div>
        </div>
    );
}

function CaseList({ rows, onClose, onPick }) {
    return (
        <ul className="divide-y divide-slate-100">
            {rows.map((r) => {
                const caseId = r.id || r.case_number;
                const hasLink = !!caseId;
                // If the parent owns the click (legacy contract) call it; otherwise
                // wrap in a real <Link> so the case page opens.
                const Outer = onPick || !hasLink ? "li" : Link;
                const outerProps = onPick || !hasLink
                    ? { onClick: () => onPick?.(r) }
                    : { to: `/cases/${caseId}`, onClick: onClose };
                return (
                    <Outer
                        key={caseId}
                        {...outerProps}
                        className={`block px-5 py-3 hover:bg-slate-50/50 ${hasLink ? "cursor-pointer" : ""}`}
                        data-testid={`drill-case-${r.case_number || r.id}`}
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono-tr text-[12.5px] text-slate-900">{r.case_number}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10.5px] ${STATUS_TINTS[r.status] || "bg-slate-100 text-slate-700"}`}>{(r.status || "").replace(/_/g, " ")}</span>
                                </div>
                                <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{r.clinic_name || r.client_name || "—"} · {r.product || "—"} · {fmtDate(r.accepted_at || r.created_at)}</div>
                            </div>
                            {hasLink && (
                                <span className="inline-flex items-center gap-1 shrink-0 text-[11.5px] text-cyan-700 font-medium hover:text-cyan-900">
                                    View case <ArrowRight size={12} />
                                </span>
                            )}
                            {onPick && !hasLink && <ExternalLink size={13} className="text-slate-400 shrink-0" />}
                        </div>
                    </Outer>
                );
            })}
        </ul>
    );
}

function ClientList({ rows, onClose }) {
    // Clients aren't a routable page on their own — open the Admin Clients tab
    // and emit the existing admin-switch-tab event so the user lands exactly
    // where they can act on the row.
    const openInApp = (c) => {
        window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: { tabId: "clients" } }));
        window.dispatchEvent(new CustomEvent("admin-open-client", { detail: { clientId: c.id, email: c.email } }));
        onClose?.();
    };
    return (
        <ul className="divide-y divide-slate-100">
            {rows.map((c) => (
                <li key={c.id || c.email} className="px-5 py-3 hover:bg-slate-50/50" data-testid={`drill-client-${c.id || c.email}`}>
                    <button type="button" onClick={() => openInApp(c)} className="w-full text-left">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[12.5px] text-slate-900 font-medium truncate">{c.clinic_name || c.dentist_name || c.email}</span>
                                    {c.active === false && <span className="px-2 py-0.5 rounded-full text-[10.5px] bg-slate-100 text-slate-500">inactive</span>}
                                </div>
                                <div className="text-[11.5px] text-slate-500 truncate mt-0.5">
                                    {c.dentist_name ? `${c.dentist_name} · ` : ""}{c.email}{c.city ? ` · ${c.city}` : ""}{c.country ? `, ${c.country}` : ""}
                                </div>
                            </div>
                            <span className="inline-flex items-center gap-1 shrink-0 text-[11.5px] text-cyan-700 font-medium hover:text-cyan-900">
                                Open <ArrowRight size={12} />
                            </span>
                        </div>
                    </button>
                </li>
            ))}
        </ul>
    );
}


function InvoiceList({ rows, onClose }) {
    // For each invoice row we provide TWO actions: open the PDF (new tab, public
    // URL since /invoices/{n}/pdf needs no auth), or open the in-app modal by
    // emitting the admin-switch-tab + admin-open-invoice events.
    const openInApp = (inv) => {
        window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: { tabId: "invoices" } }));
        window.dispatchEvent(new CustomEvent("admin-open-invoice", { detail: { invoiceId: inv.id, invoiceNumber: inv.invoice_number } }));
        onClose?.();
    };
    return (
        <ul className="divide-y divide-slate-100">
            {rows.map((inv) => {
                const pdfUrl = inv.invoice_number ? `${API}/invoices/${inv.invoice_number}/pdf` : null;
                return (
                    <li key={inv.id || inv.invoice_number} className="px-5 py-3 hover:bg-slate-50/50" data-testid={`drill-invoice-${inv.invoice_number || inv.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <button
                                type="button"
                                onClick={() => openInApp(inv)}
                                className="min-w-0 flex-1 text-left"
                                data-testid={`drill-invoice-open-${inv.invoice_number || inv.id}`}
                            >
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono-tr text-[12.5px] text-slate-900">{inv.invoice_number || "—"}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10.5px] ${INVOICE_STATUS_TINTS[inv.status] || "bg-slate-100 text-slate-700"}`}>{inv.status || "—"}</span>
                                </div>
                                <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{inv.clinic_name || "—"} · {fmtCurrency(inv.total, inv.currency)} · {fmtDate(inv.issue_date || inv.created_at)}</div>
                            </button>
                            <div className="flex flex-col items-end gap-1 shrink-0 text-[11.5px]">
                                <button
                                    onClick={() => openInApp(inv)}
                                    className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 font-medium"
                                >
                                    Open <ArrowRight size={11} />
                                </button>
                                {pdfUrl && (
                                    <a
                                        href={pdfUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
                                        data-testid={`drill-invoice-pdf-${inv.invoice_number}`}
                                    >
                                        PDF <ExternalLink size={10} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Mini KPI panel rendered in the dimmed backdrop area on lg+ screens. Gives the
 * user something useful to see while the drill list is open — total count,
 * status histogram, optional total value, oldest record.
 */
function DrillSummary({ kind, title, rows, loading }) {
    const stats = useMemo(() => {
        if (!rows.length) return null;
        const total = rows.length;
        const byStatus = rows.reduce((acc, r) => {
            const s = r.status || "unknown";
            acc[s] = (acc[s] || 0) + 1;
            return acc;
        }, {});
        const totalValue = rows.reduce((a, r) => a + Number(r.total || r.amount || 0), 0);
        const dates = rows.map((r) => r.accepted_at || r.created_at || r.issue_date).filter(Boolean).sort();
        const oldest = dates[0];
        const newest = dates[dates.length - 1];
        return { total, byStatus, totalValue, oldest, newest };
    }, [rows]);

    return (
        <div className="bg-white/95 backdrop-blur rounded-2xl border border-slate-200 shadow-xl p-6" data-testid="drill-summary-panel">
            <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">· Drill snapshot</div>
            <h3 className="font-display text-xl tracking-tight text-slate-900 mt-1">{title}</h3>

            {loading ? (
                <div className="flex items-center gap-2 mt-6 text-[13px] text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
            ) : !stats ? (
                <p className="text-[13px] text-slate-500 mt-4">No records to summarise.</p>
            ) : (
                <div className="mt-5 space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                        <StatTile icon={kind === "invoices" ? FileText : Building2} label="Total" value={stats.total} />
                        {(stats.totalValue > 0) && (
                            <StatTile icon={DollarSign} label="Sum value" value={fmtCurrency(stats.totalValue)} />
                        )}
                    </div>

                    {Object.keys(stats.byStatus).length > 0 && (
                        <div>
                            <div className="font-mono-tr text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-2">Status breakdown</div>
                            <div className="space-y-1.5">
                                {Object.entries(stats.byStatus).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
                                    const pct = (count / stats.total) * 100;
                                    return (
                                        <div key={status} className="flex items-center gap-2">
                                            <span className="text-[11.5px] text-slate-600 w-28 truncate">{status.replace(/_/g, " ")}</span>
                                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                            <span className="text-[11.5px] font-mono-tr text-slate-600 w-7 text-right">{count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {(stats.oldest || stats.newest) && (
                        <div className="grid grid-cols-2 gap-3">
                            {stats.oldest && <StatTile icon={Calendar} label="Oldest" value={fmtDate(stats.oldest)} />}
                            {stats.newest && <StatTile icon={Calendar} label="Most recent" value={fmtDate(stats.newest)} />}
                        </div>
                    )}

                    <div className="text-[11px] text-slate-400 pt-3 border-t border-slate-100">Click anywhere outside to close. Each row on the right is clickable.</div>
                </div>
            )}
        </div>
    );
}

function StatTile({ icon: Icon, label, value }) {
    return (
        <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-200">
            <div className="flex items-center gap-1.5">
                <Icon size={11} className="text-slate-400" />
                <span className="font-mono-tr text-[9.5px] tracking-[0.18em] uppercase text-slate-500">{label}</span>
            </div>
            <div className="font-display text-lg tracking-tight text-slate-900 mt-1 truncate">{value}</div>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
}

function fmtCurrency(n, currency = "QAR") {
    if (!n && n !== 0) return "—";
    return `${currency} ${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}
